import React, { View } from "react-native";
import { AdminContainer } from "../../container";
import {
    Alert,
    Button,
    DataTable,
    Headline,
    Loading,
    Spacer,
    StyleFunction,
    Surface,
    useForm,
    useThemedStyle,
} from "@venuepos/react-common";
import { AvailableLocale, useTranslation } from "locales";
import {
    defaultDSFinVKExportForm,
    schemaDSFinVKExportForm,
} from "./validation";
import { Form } from "./form";
import { useCallback, useEffect } from "react";
import { useHandleMutationError } from "../../../hooks";
import {
    GQExportState,
    useFiskalyClientsQuery,
    useFiskalyDsFinVkExportsQuery,
    useGetFiskalyDsFinVkExportDownloadLazyQuery,
    useRequestFiskalyDsFinVkExportMutation,
} from "graphql-sdk";
import { formatDate, formatDateTime } from "lib";
import { useAdminSession } from "../../../session";

export function DSFinVKExportScreen() {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ locale }] = useAdminSession(["locale"]);

    const form = useForm(schemaDSFinVKExportForm, defaultDSFinVKExportForm);

    const [{ values }] = form;

    const {
        data: clientsData,
        loading: clientsLoading,
        error: clientsError,
    } = useFiskalyClientsQuery();
    const { data, loading, error, refetch } = useFiskalyDsFinVkExportsQuery({
        fetchPolicy: "no-cache",
    });
    const [getExportDownload, exportDownloadData] =
        useGetFiskalyDsFinVkExportDownloadLazyQuery();
    const [requestDsFinVkExport, { loading: mutationLoading }] =
        useRequestFiskalyDsFinVkExportMutation();
    const { handleMutationError } = useHandleMutationError();

    const save = useCallback(async () => {
        if (!values) {
            return;
        }

        await handleMutationError(
            async () => {
                await requestDsFinVkExport({
                    variables: {
                        exportArguments: {
                            fromDate: values.from,
                            toDate: values.to,
                            clientId: values.clientId || null,
                        },
                    },
                });
            },
            t("common.ok", "OK"),
            () => refetch()
        );
    }, [handleMutationError, refetch, requestDsFinVkExport, t, values]);

    useEffect(() => {
        if (
            !exportDownloadData ||
            exportDownloadData.loading ||
            !exportDownloadData.data
        ) {
            return;
        }

        const { fileName, contentType, byteBuffer } =
            exportDownloadData.data.getFiskalyDSFinVKExportDownload;
        const blob = createBlobFromByteBuffer(byteBuffer, contentType);
        downloadFile(blob, fileName);
    }, [exportDownloadData]);

    return (
        <AdminContainer>
            <Surface>
                <View style={styles.container}>
                    <Headline size="h1">
                        {t(
                            "backoffice.integration.fiskaly.new_export",
                            "New export"
                        )}
                    </Headline>
                    <Form
                        form={form}
                        onSubmit={save}
                        submitButton={["common.save", "Save"]}
                        requestRunning={mutationLoading}
                        clients={clientsData}
                    />
                </View>
            </Surface>
            <Spacer />
            <Surface>
                <Headline size="h1">
                    {t(
                        "backoffice.integration.fiskaly.export_list",
                        "List of DSFinV-K exports"
                    )}
                </Headline>
                <DataTable>
                    <DataTable.Header>
                        <DataTable.Title>
                            {t("backoffice.common.from", "From")}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t("backoffice.common.to", "To")}
                        </DataTable.Title>
                        <DataTable.Title>{`${t(
                            "backoffice.integration.fiskaly.client",
                            "Client"
                        )}?`}</DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.integration.fiskaly.export_state",
                                "State"
                            )}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t("backoffice.common.download", "Download")}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t(
                                "backoffice.integration.fiskaly.expires_at",
                                "Expires at"
                            )}
                        </DataTable.Title>
                        <DataTable.Title>
                            {t("backoffice.integration.fiskaly.error", "Error")}
                        </DataTable.Title>
                    </DataTable.Header>
                    {error ? (
                        <Alert type="error">
                            {" "}
                            {t(
                                "backoffice.error.from_server",
                                "There was an error: {{errorText}}",
                                {
                                    errorText: error.message,
                                }
                            )}
                        </Alert>
                    ) : null}
                    {clientsError ? (
                        <Alert type="error">
                            {" "}
                            {t(
                                "backoffice.error.from_server",
                                "There was an error: {{errorText}}",
                                {
                                    errorText: clientsError.message,
                                }
                            )}
                        </Alert>
                    ) : null}
                    {loading || clientsLoading ? <Loading /> : null}
                    {data?.fiskalyDSFinVKExports.data.map((itr, index) => (
                        <DataTable.Row
                            style={index % 2 === 1 ? styles.oddRow : undefined}
                            key={`export_row_${index}`}
                        >
                            <DataTable.Cell>
                                {formatDateTime(
                                    itr.fromDate,
                                    locale as AvailableLocale
                                )}
                            </DataTable.Cell>
                            <DataTable.Cell>
                                {formatDateTime(
                                    itr.toDate,
                                    locale as AvailableLocale
                                )}
                            </DataTable.Cell>
                            <DataTable.Cell>{itr.clientId}</DataTable.Cell>
                            <DataTable.Cell>{itr.exportState}</DataTable.Cell>
                            <DataTable.Cell>
                                {!itr.completedAt ||
                                itr.exportState !==
                                    GQExportState.Completed ? null : (
                                    <Button
                                        size="small"
                                        onPress={() => {
                                            getExportDownload({
                                                variables: {
                                                    id: itr.id,
                                                },
                                                fetchPolicy: "no-cache",
                                            });
                                        }}
                                        style={styles.download}
                                    >
                                        {t(
                                            "backoffice.common.download",
                                            "Download"
                                        )}
                                    </Button>
                                )}
                            </DataTable.Cell>
                            <DataTable.Cell>
                                {itr.expireAt
                                    ? formatDate(
                                          itr.expireAt,
                                          locale as AvailableLocale
                                      )
                                    : null}
                            </DataTable.Cell>
                            <DataTable.Cell>
                                {itr.error ? itr.error : null}
                            </DataTable.Cell>
                        </DataTable.Row>
                    ))}
                </DataTable>
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: { maxWidth: theme.dimensions.maxFormWidth },
    link: {
        ...theme.styles.link,
    },
    download: {
        maxWidth: theme.spacingScale * 12,
    },
    oddRow: {
        ...theme.styles.oddRow,
    },
});

export const b64toBlob = (b64Data, contentType = "", sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

// Source - https://stackoverflow.com/a
// Posted by Zachary Betz, modified by community. See post 'Timeline' for change history
// Retrieved 2025-12-08, License - CC BY-SA 4.0

export const createBlobFromByteBuffer = (
    byteBuffer: { data: any },
    contentType: string
): Blob => {
    return new Blob([new Uint8Array(byteBuffer.data)], { type: contentType });
};

export const downloadFile = (blob: Blob, filename: string): void => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
};
